import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { frappeApi } from '../services/frappeApi';
import '../styles/ProjectDetail.css';

const ProjectDetail = () => {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const publicUrl = process.env.PUBLIC_URL || '';

  // Static projects fallback
  const staticProjects = {
    'hosting-erpnext': {
      title: 'Hosting ERPNext',
      description: 'Profesjonalny hosting i zarządzanie instancjami ERPNext w chmurze, zapewniający wysoką dostępność i bezpieczeństwo danych.',
      content: `## O projekcie\n\nHosting ERPNext to kompleksowe rozwiązanie do hostowania instancji ERPNext w chmurze.\n\n### Funkcje:\n- Pełne zarządzanie instancjami\n- Automatyczne backupy\n- Wsparcie techniczne 24/7\n- Wysoka dostępność i bezpieczeństwo`,
      image: `${publicUrl}/images/hosting-erpnext.webp`,
      tags: ['Frappe', 'Python', 'Docker']
    },
    'ulszkolna': {
      title: 'ulSzkolna.pl',
      description: 'Projekt stworzony dla uczniów, oferujący zasoby edukacyjne i narzędzia do nauki online.',
      content: `## O projekcie\n\nulSzkolna.pl był platformą edukacyjną stworzoną z myślą o uczniach.\n\n### Funkcje:\n- Zasoby edukacyjne\n- Narzędzia do nauki online\n- Społeczność uczniowska`,
      image: `${publicUrl}/images/ulszkolna.webp`,
      tags: ['Vue.js', 'TypeScript', 'Discord Management']
    },
    'litty': {
      title: 'Litty',
      description: 'Platforma komunikacyjna w czasie rzeczywistym umożliwiająca tworzenie serwerów społecznościowych, czaty, zarządzanie znajomymi.',
      content: `## O projekcie\n\nLitty to nowoczesna platforma komunikacyjna inspirowana Discord.\n\n### Funkcje:\n- Serwery społecznościowe\n- Czaty w czasie rzeczywistym\n- Zarządzanie znajomymi\n- Panel administracyjny`,
      image: `${publicUrl}/images/litty-dashboard.webp`,
      tags: ['Vue.js', 'TypeScript', 'PostgreSQL', 'Chart.js']
    }
  };

  useEffect(() => {
    const fetchProject = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const blogPost = await frappeApi.getBlogPost(slug);
        
        if (blogPost) {
          setProject({
            title: blogPost.title,
            description: blogPost.blog_intro || '',
            content: blogPost.content_md || blogPost.content || blogPost.content_html || '',
            image: blogPost.meta_image || `${publicUrl}/images/default-project.webp`,
            published_on: blogPost.published_on,
            author: blogPost.blogger || 'Mespi Team',
            meta_description: blogPost.meta_description,
            tags: blogPost.tags ? blogPost.tags.split(',').map(t => t.trim()) : []
          });
          // Update page title dynamically
          document.title = `${blogPost.title} - Mespi.dev`;
        } else {
          setError('Projekt nie został znaleziony');
        }
      } catch (err) {
        console.error('Error loading project:', err);
        
        // Try static projects fallback
        if (staticProjects[slug]) {
          const staticProject = staticProjects[slug];
          setProject({
            title: staticProject.title,
            description: staticProject.description,
            content: staticProject.content,
            image: staticProject.image,
            tags: staticProject.tags,
            author: 'Mespi Team'
          });
          document.title = `${staticProject.title} - Mespi.dev`;
        } else {
          setError('Wystąpił błąd podczas ładowania projektu');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (loading) {
    return (
      <div className="App">
        <Navigation />
        <section className="project-detail-section">
          <div className="container">
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Ładowanie projektu...</p>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="App">
        <Navigation />
        <section className="project-detail-section">
          <div className="container">
            <div className="error-container">
              <h2 className="error-title">😕 {error || 'Projekt nie został znaleziony'}</h2>
              <Link to="/projekty" className="back-link">
                ← Wróć do listy projektów
              </Link>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="App">
      <Navigation />
      <section className="project-detail-section">
        <div className="container">
          <Link to="/projekty" className="back-link">
            ← Wróć do listy projektów
          </Link>
          
          <article className="project-detail">
            <header className="project-header">
              <h1 className="project-title-detail">{project.title}</h1>
              {project.description && (
                <p className="project-intro">{project.description}</p>
              )}
              <div className="project-meta">
                {project.published_on && (
                  <span className="meta-item">
                    📅 {new Date(project.published_on).toLocaleDateString('pl-PL', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                )}
                {project.author && (
                  <span className="meta-item">
                    👤 {project.author}
                  </span>
                )}
              </div>
            </header>

            {project.image && (
              <div className="project-hero-image">
                <img 
                  src={project.image} 
                  alt={project.title}
                  onError={(e) => {
                    e.target.src = `${publicUrl}/images/default-project.webp`;
                  }}
                />
              </div>
            )}

            <div className="project-content">
              <ReactMarkdown
                components={{
                  // eslint-disable-next-line jsx-a11y/heading-has-content
                  h1: ({ node, ...props }) => <h2 className="content-h1" {...props} />,
                  // eslint-disable-next-line jsx-a11y/heading-has-content
                  h2: ({ node, ...props }) => <h3 className="content-h2" {...props} />,
                  // eslint-disable-next-line jsx-a11y/heading-has-content
                  h3: ({ node, ...props }) => <h4 className="content-h3" {...props} />,
                  p: ({ node, ...props }) => <p className="content-p" {...props} />,
                  // eslint-disable-next-line jsx-a11y/anchor-has-content
                  a: ({ node, ...props }) => <a className="content-link" target="_blank" rel="noopener noreferrer" {...props} />,
                  ul: ({ node, ...props }) => <ul className="content-ul" {...props} />,
                  ol: ({ node, ...props }) => <ol className="content-ol" {...props} />,
                  li: ({ node, ...props }) => <li className="content-li" {...props} />,
                  code: ({ node, inline, ...props }) => 
                    inline ? 
                      <code className="content-code-inline" {...props} /> : 
                      <code className="content-code-block" {...props} />,
                  pre: ({ node, ...props }) => <pre className="content-pre" {...props} />,
                  blockquote: ({ node, ...props }) => <blockquote className="content-blockquote" {...props} />,
                  // eslint-disable-next-line jsx-a11y/alt-text
                  img: ({ node, ...props }) => <img className="content-img" {...props} />,
                }}
              >
                {project.content}
              </ReactMarkdown>
            </div>

            {project.tags && project.tags.length > 0 && (
              <footer className="project-footer">
                <div className="project-tags">
                  <span className="tags-label">Tagi:</span>
                  {project.tags.map((tag, index) => (
                    <span key={index} className="tag-item">{tag}</span>
                  ))}
                </div>
              </footer>
            )}
          </article>

          <div className="project-navigation">
            <Link to="/projekty" className="nav-button">
              Zobacz wszystkie projekty
            </Link>
            <a href="/#contact" className="nav-button primary">
              Skontaktuj się z nami
            </a>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default ProjectDetail;
